import { useFeatureFlagEnabled } from '@posthog/react'

export const FEATURE_FLAG_OVERRIDES_KEY = 'posthog_flag_overrides'

export type FeatureFlagOverrides = Record<string, boolean>

export const RETIRED_FEATURE_FLAGS = new Set([
  'calendar-service',
  'companies',
  'crm-enhanced',
  'crm-service',
  'quick-add',
])

export function removeRetiredFeatureFlags(overrides: FeatureFlagOverrides): FeatureFlagOverrides {
  return Object.fromEntries(Object.entries(overrides).filter(([flag]) => !RETIRED_FEATURE_FLAGS.has(flag)))
}

export function loadFeatureFlagOverrides(): FeatureFlagOverrides {
  if (typeof window === 'undefined') return {}

  try {
    const stored = window.localStorage.getItem(FEATURE_FLAG_OVERRIDES_KEY) || '{}'
    const parsed = JSON.parse(stored)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const loadedOverrides = Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'),
    )
    const activeOverrides = removeRetiredFeatureFlags(loadedOverrides)
    if (JSON.stringify(activeOverrides) !== JSON.stringify(loadedOverrides)) {
      saveFeatureFlagOverrides(activeOverrides)
    }
    return activeOverrides
  } catch {
    return {}
  }
}

export function saveFeatureFlagOverrides(overrides: FeatureFlagOverrides) {
  window.localStorage.setItem(FEATURE_FLAG_OVERRIDES_KEY, JSON.stringify(overrides))
}

export function getLocalFeatureFlagOverride(flag: string): boolean | undefined {
  if (RETIRED_FEATURE_FLAGS.has(flag)) return undefined
  return loadFeatureFlagOverrides()[flag]
}

export function useEffectiveFeatureFlagEnabled(flag: string): boolean {
  const remoteEnabled = useFeatureFlagEnabled(flag)
  return getLocalFeatureFlagOverride(flag) ?? remoteEnabled ?? false
}
